import { FormTextField } from '@map/components/formControls/controlled';
import { makeStyles } from '@map/theme';
import { windowExtended } from '@map/types';
import useToggleState from '@shared/hooks/useToggleState';
import { Form } from '@shared/lib/form';
import { SubmitButton, type SubmitFunction } from '@shared/lib/form/Form';
import Yup from '@shared/lib/yupWithCustomValidations';
import { getNavigatorLanguage } from '@shared/util/locale';
import Button from '@ui/Button';
import { FormattedMessage } from 'react-intl';
import PublicPage from '../shared/PublicPage';
import PublicPageHeader from '../shared/PublicPageHeader';
import MissingCodeDialog from './MissingCodeDialog';

const useStyles = makeStyles(({ spacing }) => ({
  button: {
    marginTop: spacing(4),
    marginBottom: spacing(4),
  },
}));

const validationSchema = Yup.object({
  access_code: Yup.string().notBlank(),
});

const AccessCodeForm = ({
  onSubmit,
}: {
  onSubmit: SubmitFunction<{ access_code: string }>;
}) => {
  const { classes } = useStyles();
  const [missingCodeOpen, showMissingCode, hideMissingCode] = useToggleState();
  // We disable the autoFocus when the language is not supported,
  // because autoFocus doesn't work with "google translation banner"
  // if the language is supported, we disable google translation
  const isNavigatorLanguageSupported = !!getNavigatorLanguage();

  return (
    <PublicPage>
      <PublicPageHeader
        title={<FormattedMessage id="registration.access_code.title" />}
        subtitle={<FormattedMessage id="registration.access_code.subtitle" />}
      />
      <Form
        validationMode="onBlur"
        initialValues={{ access_code: windowExtended.project.access_code || '' }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <FormTextField
          name="access_code"
          autoComplete="off"
          margin="none"
          autoCapitalize="none"
          autoFocus={isNavigatorLanguageSupported}
        />

        <SubmitButton fullWidth={true} size="medium" className={classes.button}>
          <FormattedMessage id="registration.next" />
        </SubmitButton>
      </Form>
      <Button variant="tertiary" size="medium" onClick={showMissingCode} fullWidth={true}>
        <FormattedMessage id="registration.access_code.help_link" />
      </Button>
      {missingCodeOpen && <MissingCodeDialog onClose={hideMissingCode} />}
    </PublicPage>
  );
};

export default AccessCodeForm;
